import { useEffect, useRef, useState } from 'react';
import { auth } from '../lib/api';

/**
 * Showcase Yourself controlled component, injected via the engine's
 * `showcaseComponent` prop (the render-only engine stubs it otherwise).
 * Value shape is v1's step_data contract:
 *   { video_introduction?, photo_gallery?: Array<PhotoRef|string>, photo_captions? }
 * onChange receives PARTIALS merged into the field value — updating the video
 * must never clobber the gallery (v1 adapter rule, kept).
 *
 * Per-photo controls mirror the engine's AdvancedFileUpload gallery that this
 * component replaced: rotate, rename, drag-to-reorder, caption, remove, plus a
 * moderation badge when the host supplies one. Rotation/displayName persist on
 * the photo ref so they survive a reload (the engine's rotation was ephemeral).
 * Byte-identical duplicates are rejected via a SHA-256 of the file.
 */
export interface PhotoRef {
  url: string;
  filename?: string;
  /** User-facing name (rename control); falls back to filename. */
  displayName?: string;
  /** Display rotation in degrees (0/90/180/270) — CSS transform, persisted. */
  rotation?: number;
  /** SHA-256 of the uploaded bytes, used to reject duplicate uploads. */
  hash?: string;
  /** Host moderation status (e.g. Globus photo review), rendered as a badge. */
  approvalStatus?: string;
  rejectionReason?: string;
}
export interface ShowcaseValue {
  video_introduction?: string;
  photo_gallery?: Array<PhotoRef | string>;
  photo_captions?: Record<string, string>;
}
export interface ShowcaseProps {
  /** Current field value. The engine's ShowcaseYourselfRenderer passes this as
   * `defaultValue` (matching the built-in ShowcaseComposite contract) and
   * re-passes the latest value on every render, so this component is
   * controlled-by-parent — read `defaultValue`, falling back to the legacy
   * `value` prop name. Reading only `value` left the gallery permanently empty
   * (uploaded photos never rendered) because the engine never passes `value`. */
  value?: ShowcaseValue;
  defaultValue?: ShowcaseValue;
  onChange: (partial: Record<string, unknown>) => void;
  onValidationChange?: (isValid: boolean) => void;
  showValidationErrors?: boolean;
  isReadOnly?: boolean;
  uploadPhoto: (file: File) => Promise<{ downloadUrl?: string; fileName?: string }>;
  /** Optional hard-delete callback, AWAITED before the photo is spliced out
   * of the gallery. Absent → splice-only (legacy behavior). */
  deletePhoto?: (ref: PhotoRef | string) => Promise<void>;
}

const MIN_PHOTOS = 3;
const MAX_PHOTOS = 50;

// The video introduction is OPTIONAL — an empty value never blocks the step.
// When a link IS provided, accept any http(s) URL (matching the Globus field's
// own `^https?://.+` pattern) rather than restricting to specific platforms; the
// old YouTube/Vimeo/Drive-only rule hard-disabled Save for legitimate links
// (Loom, scheme-less paste, etc.), which made the field feel mandatory.
const isValidVideoUrl = (url: string) => /^https?:\/\/\S+$/i.test(url.trim());

const asRef = (p: PhotoRef | string): PhotoRef => (typeof p === 'string' ? { url: p } : p);
const refUrl = (p: PhotoRef | string) => asRef(p).url;
/** Stable name — the caption map key; NOT changed by rename (rename edits displayName). */
const refName = (p: PhotoRef | string) => {
  const r = asRef(p);
  return r.filename ?? r.url.split('/').pop() ?? 'photo';
};
const refDisplay = (p: PhotoRef | string) => asRef(p).displayName ?? refName(p);

/** SHA-256 hex of a file's bytes (secure-context Web Crypto). */
async function sha256(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function ShowcaseYourself({
  value,
  defaultValue,
  onChange,
  onValidationChange,
  showValidationErrors,
  isReadOnly,
  uploadPhoto,
  deletePhoto,
}: ShowcaseProps) {
  const current = defaultValue ?? value;
  const gallery = current?.photo_gallery ?? [];
  const captions = current?.photo_captions ?? {};
  const video = current?.video_introduction ?? '';
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [removingIdx, setRemovingIdx] = useState<number | null>(null);
  const [error, setError] = useState<string>();
  const [editing, setEditing] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // Photos are required; validate the optional video only when supplied.
  const valid = gallery.length >= MIN_PHOTOS && (!video.trim() || isValidVideoUrl(video));
  useEffect(() => onValidationChange?.(valid), [valid, onValidationChange]);

  /** Update one gallery entry (normalising string refs to objects). */
  function patchPhoto(idx: number, patch: Partial<PhotoRef>) {
    const next = gallery.map((p, i) => (i === idx ? { ...asRef(p), ...patch } : p));
    onChange({ photo_gallery: next });
  }

  async function addPhotos(files: FileList | null) {
    if (!files?.length || isReadOnly) return;
    if (gallery.length + files.length > MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos.`);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      // Reject byte-identical duplicates (against the existing gallery and within
      // this batch) so the same image can't be added twice.
      const seen = new Set(gallery.map((p) => asRef(p).hash).filter(Boolean) as string[]);
      const added: PhotoRef[] = [];
      let duplicates = 0;
      for (const f of Array.from(files)) {
        const hash = await sha256(f).catch(() => undefined);
        if (hash && seen.has(hash)) {
          duplicates++;
          continue;
        }
        const res = await uploadPhoto(f);
        added.push({ url: res.downloadUrl ?? '', filename: res.fileName ?? f.name, hash, rotation: 0 });
        if (hash) seen.add(hash);
      }
      if (added.length) onChange({ photo_gallery: [...gallery, ...added] });
      if (duplicates) {
        setError(
          `Skipped ${duplicates} photo${duplicates > 1 ? 's' : ''} already in your gallery.`,
        );
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      // Clear the input so re-selecting the SAME file still fires `change`
      // (browsers suppress it when the value is unchanged) — otherwise a
      // re-pick of an identical photo would be a silent no-op instead of
      // showing the duplicate-skipped message.
      if (input.current) input.current.value = '';
    }
  }

  async function removePhoto(idx: number) {
    if (removingIdx !== null) return;
    const target = gallery[idx];
    if (deletePhoto) {
      setRemovingIdx(idx);
      setError(undefined);
      try {
        await deletePhoto(target);
      } catch (e) {
        setError((e as Error).message);
        setRemovingIdx(null);
        return;
      }
      setRemovingIdx(null);
    }
    const next = gallery.filter((_, i) => i !== idx);
    const removed = refName(target);
    const nextCaptions = { ...captions };
    delete nextCaptions[removed];
    onChange({ photo_gallery: next, photo_captions: nextCaptions });
  }

  function rotatePhoto(idx: number) {
    patchPhoto(idx, { rotation: ((asRef(gallery[idx]).rotation ?? 0) + 90) % 360 });
  }

  function reorder(from: number, to: number) {
    if (from === to) return;
    const next = [...gallery];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange({ photo_gallery: next });
  }

  function commitRename(idx: number) {
    const name = editName.trim();
    patchPhoto(idx, { displayName: name || undefined });
    setEditing(null);
    setEditName('');
  }

  return (
    <div className="showcase">
      <h3>
        Photo gallery{' '}
        <span className={`showcase-count${gallery.length < MIN_PHOTOS ? ' short' : ' ok'}`}>
          {gallery.length < MIN_PHOTOS
            ? `— add ${MIN_PHOTOS - gallery.length} more (${gallery.length} of ${MIN_PHOTOS} required)`
            : `✓ ${gallery.length} added (${MIN_PHOTOS} required)`}
        </span>
      </h3>
      <div className="showcase-grid">
        {gallery.map((p, i) => {
          const ref = asRef(p);
          const status = ref.approvalStatus;
          return (
            <figure
              key={`${refUrl(p)}-${i}`}
              className={`showcase-item${dragIdx === i ? ' dragging' : ''}`}
              draggable={!isReadOnly && editing === null}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIdx !== null) reorder(dragIdx, i);
                setDragIdx(null);
              }}
              onDragEnd={() => setDragIdx(null)}
            >
              <div className="showcase-thumb">
                <AuthImage path={refUrl(p)} alt={refDisplay(p)} rotation={ref.rotation ?? 0} />
                {status && status !== 'approved' && status !== 'pending' && (
                  <span
                    className={`showcase-badge ${status === 'rejected' ? 'rejected' : 'flagged'}`}
                    title={ref.rejectionReason ?? status}
                  >
                    {status}
                  </span>
                )}
                {!isReadOnly && (
                  <div className="showcase-actions">
                    <button type="button" title="Rename photo" aria-label="Rename photo"
                      onClick={() => { setEditing(i); setEditName(refDisplay(p)); }}>
                      ✎
                    </button>
                    <button type="button" title="Rotate photo" aria-label="Rotate photo" onClick={() => rotatePhoto(i)}>
                      ↻
                    </button>
                    <button type="button" className="danger" title="Remove photo" aria-label="Remove photo"
                      disabled={removingIdx === i} onClick={() => void removePhoto(i)}>
                      ×
                    </button>
                  </div>
                )}
              </div>
              {editing === i ? (
                <input
                  type="text"
                  autoFocus
                  className="showcase-rename"
                  value={editName}
                  placeholder="Photo name"
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => commitRename(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename(i);
                    else if (e.key === 'Escape') {
                      setEditing(null);
                      setEditName('');
                    }
                  }}
                />
              ) : (
                <figcaption className="showcase-name" title={refDisplay(p)}>{refDisplay(p)}</figcaption>
              )}
              <input
                type="text"
                placeholder="Add a caption"
                disabled={isReadOnly}
                value={captions[refName(p)] ?? ''}
                onChange={(e) => onChange({ photo_captions: { ...captions, [refName(p)]: e.target.value } })}
              />
            </figure>
          );
        })}
        {!isReadOnly && gallery.length < MAX_PHOTOS && (
          <button type="button" className="showcase-add" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Uploading…' : '+ Add photos'}
          </button>
        )}
      </div>
      <input ref={input} type="file" hidden multiple accept="image/*" onChange={(e) => void addPhotos(e.target.files)} />

      <h3>Video introduction (optional)</h3>
      <input
        type="url"
        placeholder="Paste a link to your video — e.g. YouTube, Vimeo, Loom, Google Drive"
        disabled={isReadOnly}
        value={video}
        onChange={(e) => onChange({ video_introduction: e.target.value })}
      />
      {video.trim() && !isValidVideoUrl(video) && (
        <p className="showcase-fielderr" role="alert">Enter a valid link starting with http:// or https://</p>
      )}
      {error && <p className="showcase-fielderr" role="alert">{error}</p>}

      {/* Missing photos stay visible; invalid optional links have inline feedback above. */}
      {gallery.length < MIN_PHOTOS && (
        <div className={`showcase-reqs${showValidationErrors ? ' attempted' : ''}`} role={showValidationErrors ? 'alert' : 'status'}>
          <strong>To continue, please add:</strong>
          <ul>
            {gallery.length < MIN_PHOTOS && (
              <li>
                At least {MIN_PHOTOS} photos — you’ve added {gallery.length} of {MIN_PHOTOS}
                {gallery.length > 0 ? ` (${MIN_PHOTOS - gallery.length} more needed)` : ''}.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Gallery thumbnails live behind the authenticated BFF — plain <img src> can't send the Bearer. */
function AuthImage({ path, alt, rotation = 0 }: { path: string; alt: string; rotation?: number }) {
  const [src, setSrc] = useState<string>();
  useEffect(() => {
    let url: string | undefined;
    let alive = true;
    const base = import.meta.env.VITE_API_BASE_URL ?? '';
    // Gallery photos persist the canonical Globus step-media path
    // (`/api/step-media/:id/file`) so BOP staff render them unchanged; the portal
    // streams the same object through the BFF, which mounts it under /api/v1.
    const rel = path.startsWith('/api/step-media/')
      ? path.replace('/api/step-media/', '/api/v1/step-media/')
      : path;
    fetch(`${base}${rel}`, { headers: { Authorization: `Bearer ${auth.getToken()}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => {
        url = URL.createObjectURL(b);
        if (alive) setSrc(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);
  const style = rotation ? { transform: `rotate(${rotation}deg)` } : undefined;
  return src ? <img src={src} alt={alt} style={style} /> : <span className="showcase-placeholder" aria-label={alt} />;
}
